<app-legal-page-layout
  kicker="Legal"
  title="Cookies &amp; Similar Technologies"
  description="How Safeword uses cookies and similar technologies to provide, maintain, secure and improve the Services."
  [metaLines]="['Operated by Hove Retail Limited']"
  [sections]="sections">

  <p>Safeword uses cookies and similar technologies to provide, maintain, secure and improve the Services.</p>
  <p>
    Cookies help us remember your preferences, keep you securely signed in, understand how our platform is used and
    protect both our members and the integrity of the Services.
  </p>
  <p>
    Where required by law, we will request your consent before placing non-essential cookies or similar
    technologies on your device.
  </p>
  <p>You may manage your cookie preferences at any time through our cookie management tools or by adjusting your browser settings.</p>

  <section id="what-are-cookies">
    <h2>What Are Cookies?</h2>
    <p>Cookies are small text files placed on your computer, smartphone, tablet or other device when you visit a website.</p>
    <p>Cookies allow websites to recognise your device, remember information about your visit and improve your browsing experience.</p>
    <p>In addition to cookies, Safeword may use similar technologies including:</p>
    <ul>
      <li>local storage;</li>
      <li>session storage;</li>
      <li>pixel tags;</li>
      <li>web beacons;</li>
      <li>software development kits (SDKs);</li>
      <li>application identifiers; and</li>
      <li>similar technologies used for security, analytics or functionality.</li>
    </ul>
    <p>For simplicity, these technologies are collectively referred to in this Privacy Policy as "cookies".</p>
  </section>

  <section id="types-of-cookies">
    <h2>Types of Cookies We Use</h2>
    <p>Safeword may use the following categories of cookies.</p>

    <h3>Strictly Necessary Cookies</h3>
    <p>These cookies are essential for the operation of the Services.</p>
    <p>They enable core functionality including:</p>
    <ul>
      <li>secure login;</li>
      <li>account authentication;</li>
      <li>session management;</li>
      <li>fraud prevention;</li>
      <li>load balancing;</li>
      <li>website security;</li>
      <li>payment processing support;</li>
      <li>remembering privacy preferences; and</li>
      <li>maintaining the stability of the platform.</li>
    </ul>
    <p>Because these cookies are essential to the operation of Safeword, they cannot generally be disabled through our website.</p>

    <h3>Functional Cookies</h3>
    <p>Functional cookies remember choices you make to improve your experience.</p>
    <p>These may include remembering:</p>
    <ul>
      <li>language preferences;</li>
      <li>accessibility settings;</li>
      <li>display preferences;</li>
      <li>notification preferences;</li>
      <li>consent choices;</li>
      <li>account preferences; and</li>
      <li>other optional settings.</li>
    </ul>
    <p>These cookies help personalise your experience without identifying you unnecessarily.</p>

    <h3>Analytics Cookies</h3>
    <p>Analytics cookies help us understand how members use the platform.</p>
    <p>They may collect information including:</p>
    <ul>
      <li>pages visited;</li>
      <li>time spent on pages;</li>
      <li>navigation patterns;</li>
      <li>feature usage;</li>
      <li>device type;</li>
      <li>browser type;</li>
      <li>operating system;</li>
      <li>approximate geographic region;</li>
      <li>referring websites; and</li>
      <li>general performance information.</li>
    </ul>
    <p>Analytics information helps us:</p>
    <ul>
      <li>improve the Services;</li>
      <li>identify technical issues;</li>
      <li>measure feature usage;</li>
      <li>improve performance;</li>
      <li>enhance accessibility;</li>
      <li>understand member behaviour; and</li>
      <li>develop new features.</li>
    </ul>
    <p>Where reasonably practicable, analytics information is aggregated or pseudonymised.</p>

    <h3>Security Cookies</h3>
    <p>Security-related cookies help protect the Services against abuse.</p>
    <p>They may be used to:</p>
    <ul>
      <li>detect suspicious activity;</li>
      <li>identify automated bots;</li>
      <li>prevent spam;</li>
      <li>protect against account takeover;</li>
      <li>support fraud prevention;</li>
      <li>monitor authentication attempts;</li>
      <li>detect malicious activity; and</li>
      <li>protect the integrity of the platform.</li>
    </ul>
    <p>These cookies form an important part of Safeword's security controls.</p>

    <h3>Performance Cookies</h3>
    <p>Performance cookies help us monitor the reliability of the Services.</p>
    <p>They may collect information relating to:</p>
    <ul>
      <li>page loading times;</li>
      <li>server performance;</li>
      <li>technical errors;</li>
      <li>crash reports;</li>
      <li>browser compatibility;</li>
      <li>application stability; and</li>
      <li>overall service availability.</li>
    </ul>
    <p>This information assists us in improving the reliability and performance of Safeword.</p>
  </section>

  <section id="third-party-cookies">
    <h2>Third-Party Cookies</h2>
    <p>
      Some trusted third-party service providers engaged by Safeword may use cookies or similar technologies in
      connection with the services they provide.
    </p>
    <p>Examples may include providers responsible for:</p>
    <ul>
      <li>payment processing;</li>
      <li>age verification;</li>
      <li>fraud prevention;</li>
      <li>security monitoring;</li>
      <li>website hosting;</li>
      <li>analytics;</li>
      <li>customer support;</li>
      <li>content delivery; or</li>
      <li>other technical services supporting the operation of Safeword.</li>
    </ul>
    <p>
      Where third-party cookies are used, those providers process information in accordance with their own privacy
      policies and applicable legal requirements.
    </p>
  </section>

  <section id="consent">
    <h2>Consent</h2>
    <p>
      Where UK law requires consent before cookies are placed on your device, Safeword will request your permission
      through an appropriate cookie consent mechanism.
    </p>
    <p>You may choose to:</p>
    <ul>
      <li>accept all cookies;</li>
      <li>reject non-essential cookies;</li>
      <li>customise your cookie preferences; or</li>
      <li>change your preferences at any time.</li>
    </ul>
    <p>Withdrawing consent will not affect the lawfulness of any processing carried out before consent was withdrawn.</p>
  </section>

  <section id="managing-cookies">
    <h2>Managing Cookies</h2>
    <p>Most web browsers allow you to manage cookies through their settings.</p>
    <p>You may be able to:</p>
    <ul>
      <li>delete existing cookies;</li>
      <li>block future cookies;</li>
      <li>receive notifications before cookies are stored;</li>
      <li>restrict cookies from specific websites; or</li>
      <li>remove stored website data.</li>
    </ul>
    <p>Please note that disabling certain cookies may affect the functionality, performance or availability of parts of the Safeword platform.</p>
    <p>Some Services may not operate correctly without essential cookies.</p>
  </section>

  <section id="do-not-track">
    <h2>Do Not Track</h2>
    <p>Some web browsers offer a "Do Not Track" setting.</p>
    <p>
      As there is currently no universally accepted standard governing how websites should respond to these
      signals, Safeword does not automatically alter its behaviour in response to browser-based "Do Not Track"
      requests.
    </p>
    <p>We do not sell your personal information or use cookies to build advertising profiles for third-party advertisers.</p>
  </section>

  <section id="cookie-retention">
    <h2>Cookie Retention</h2>
    <p>Some cookies remain only for the duration of your browsing session and are automatically deleted when your browser is closed.</p>
    <p>Other cookies may remain on your device for longer periods in order to remember your preferences or improve future visits.</p>
    <p>Retention periods vary depending upon the purpose of the cookie and applicable legal requirements.</p>

    <div class="legal-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Example cookie/technology</th>
            <th>Provider</th>
            <th>Purpose</th>
            <th>Typical duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Strictly Necessary</td>
            <td>Session ID cookie</td>
            <td>Safeword (first-party)</td>
            <td>Keeps you logged in and maintains session security</td>
            <td>Session / up to 24 hours</td>
          </tr>
          <tr>
            <td>Strictly Necessary</td>
            <td>Load-balancing cookie</td>
            <td>Hosting / CDN provider</td>
            <td>Routes traffic reliably across servers</td>
            <td>Session</td>
          </tr>
          <tr>
            <td>Functional</td>
            <td>Preferences cookie</td>
            <td>Safeword (first-party)</td>
            <td>Remembers language, display and consent preferences</td>
            <td>Up to 12 months</td>
          </tr>
          <tr>
            <td>Analytics</td>
            <td>Analytics client ID cookie</td>
            <td>Analytics provider</td>
            <td>Measures site usage and feature performance</td>
            <td>Up to 12–14 months</td>
          </tr>
          <tr>
            <td>Security</td>
            <td>Bot / fraud detection cookie</td>
            <td>Security / CDN provider</td>
            <td>Detects suspicious activity and automated abuse</td>
            <td>Session / up to 12 months</td>
          </tr>
          <tr>
            <td>Performance</td>
            <td>Error / crash reporting cookie</td>
            <td>Performance monitoring provider</td>
            <td>Monitors reliability, load times and technical errors</td>
            <td>Up to 12 months</td>
          </tr>
          <tr>
            <td>Third-party — Payments</td>
            <td>Fraud-prevention cookie</td>
            <td>PayFasto</td>
            <td>Supports secure payment processing and fraud checks</td>
            <td>Per PayFasto's own retention schedule</td>
          </tr>
          <tr>
            <td>Third-party — Age Verification</td>
            <td>Verification session cookie</td>
            <td>VerifyMyAge</td>
            <td>Supports the age verification process</td>
            <td>Session / short-lived</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <section id="updates">
    <h2>Updates to Our Use of Cookies</h2>
    <p>As Safeword develops new features and engages new technology providers, the cookies and similar technologies used by the platform may change.</p>
    <p>We will update this Privacy Policy and our Cookie Notice where appropriate to reflect material changes in our use of cookies.</p>
  </section>

  <section id="further-information">
    <h2>Further Information</h2>
    <p>
      If you have any questions regarding Safeword's use of cookies or similar technologies, please contact Hove
      Retail Limited using the contact details provided in our
      <a routerLink="/legal/privacy-policy">Privacy Policy</a>.
    </p>
    <p>We are committed to using cookies responsibly, transparently and in accordance with applicable UK data protection and electronic communications legislation.</p>
  </section>

</app-legal-page-layout>
